import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    IconButton,
    InputAdornment,
    List,
    ListItemButton,
    ListItemText,
    Paper,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { externalAssetService } from '../api/external-assets';
import { getNotes } from '../api/notes';
import { ProjectItem, UpsertProjectPayload, projectsService } from '../api/projects';
import { taskService } from '../api/tasks';
import {
    FilePathEntry,
    parseFilePathEntries,
    toFilePathPayloadFromEntries,
} from '../utils/filePaths';
import { OPEN_TASK_FROM_PROJECTS_EVENT, Tasks } from './Tasks';
import { FilePathEntriesEditor } from './FilePathEntriesEditor';
import { ExternalAsset } from '../types/entities/external-asset.entity';
import { Task } from '../types/entities/task.entity';
import { normalizeTxtEditorHtml } from '../components/txt-editor';
import { RichHtmlEditorField } from './RichHtmlEditorField';
import { SaveCelebrationOverlay } from './SaveCelebrationOverlay';
import { useFocusSyncToast } from '../utils/useFocusSyncToast';
import { MarkerHighlightTextField, renderTextWithMarkers } from '../utils/textMarkerHighlight';
import { SUGGESTED_PROJECT_TAGS, TagChips, TagsInput } from './workflow/TagsInput';
import { useTranslation } from 'react-i18next';
import {
    areSaveCelebrationsEnabled,
    playSaveSound,
    runConfettiBurst,
    SAVE_CELEBRATION_FLASH_MS,
} from '../utils/saveCelebrations';

type ProjectFormState = {
    name: string;
    status: string;
    description: string;
    paths: string;
    notes: string;
    assets_json: string;
};

type NoteOption = { id: string; name: string };

function dedupeNoteOptions(notes: NoteOption[]): NoteOption[] {
    const seen = new Set<string>();
    const result: NoteOption[] = [];
    for (const note of notes) {
        const id = String(note.id);
        if (seen.has(id)) {
            continue;
        }
        seen.add(id);
        result.push({ id, name: note.name ?? '' });
    }
    return result;
}

const PROJECT_STATUS_SUGGESTIONS = [
    'Planning',
    'Active',
    'On hold',
    'In progress',
    'Completed',
    'Archived',
    'Cancelled',
] as const;

const emptyForm: ProjectFormState = {
    name: '',
    status: '',
    description: '',
    paths: '',
    notes: '',
    assets_json: '',
};

const queryKey = ['projects-catalog'];
const AUTO_SAVE_DEBOUNCE_MS = 900;

const parseAssetIds = (value: string): string[] => {
    if (!value.trim()) {
        return [];
    }

    try {
        const parsed = JSON.parse(value) as { asset_ids?: unknown };
        if (!Array.isArray(parsed.asset_ids)) {
            return [];
        }
        return parsed.asset_ids
            .filter((id): id is string => typeof id === 'string' && id.trim().length > 0)
            .map((id) => id.trim());
    } catch {
        return [];
    }
};

const buildAssetsJson = (assetIds: string[]): string => {
    if (assetIds.length === 0) {
        return '';
    }
    return JSON.stringify({ asset_ids: assetIds }, null, 2);
};

const projectListHaystack = (project: ProjectItem): string => {
    const pathEntries = parseFilePathEntries(project.paths ?? '')
        .map((entry) => `${entry.label} ${entry.path}`)
        .join(' ');

    return [
        project.id,
        project.name,
        project.status,
        (project.tags ?? []).map((tag) => tag.name).join(" "),
        project.description,
        project.notes,
        project.paths,
        pathEntries,
        project.assets_json,
    ]
        .join(' ')
        .toLowerCase();
};

const sortStableIds = (ids: readonly string[]): string[] =>
    [...ids].map((id) => String(id)).sort((a, b) => a.localeCompare(b));

const sameIdList = (left: readonly string[], right: readonly string[]): boolean => {
    const a = sortStableIds(left);
    const b = sortStableIds(right);
    return a.length === b.length && a.every((value, index) => value === b[index]);
};

const buildProjectSnapshot = (
    form: ProjectFormState,
    taskIds: string[],
    noteIds: string[],
    assetIds: string[],
    pathRows: FilePathEntry[],
): string => JSON.stringify({
    name: form.name.trim(),
    status: form.status.trim(),
    description: normalizeTxtEditorHtml(form.description ?? ''),
    paths: toFilePathPayloadFromEntries(pathRows),
    notes: normalizeTxtEditorHtml(form.notes ?? ''),
    assets_json: form.assets_json,
    task_ids: sortStableIds(taskIds),
    note_ids: sortStableIds(noteIds),
    asset_ids: sortStableIds(assetIds),
});

const buildProjectSnapshotFromPayload = (payload: UpsertProjectPayload): string => JSON.stringify({
    name: payload.name.trim(),
    status: (payload.status ?? '').trim(),
    description: normalizeTxtEditorHtml(payload.description ?? ''),
    paths: payload.paths ?? '',
    notes: normalizeTxtEditorHtml(payload.notes ?? ''),
    assets_json: payload.assets_json ?? '',
    task_ids: sortStableIds(payload.task_ids ?? []),
    note_ids: sortStableIds(payload.note_ids ?? []),
    asset_ids: sortStableIds(payload.asset_ids ?? []),
});

const parseProjectUpdatedAtMs = (raw: string | null | undefined): number => {
    const ms = Date.parse(raw || '');
    return Number.isFinite(ms) ? ms : 0;
};

export const Projects = () => {
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const location = useLocation();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const [selectedProjectId, setSelectedProjectId] = useState<string>('');
    const [projectListSearch, setProjectListSearch] = useState('');
    const [projectTagFilter, setProjectTagFilter] = useState<string | null>(null);
    const [tagsSaving, setTagsSaving] = useState(false);
    const { t } = useTranslation();
    const [form, setForm] = useState<ProjectFormState>(emptyForm);
    const [selectedAssetIds, setSelectedAssetIds] = useState<string[]>([]);
    const [selectedTaskIds, setSelectedTaskIds] = useState<string[]>([]);
    const [selectedNoteIds, setSelectedNoteIds] = useState<string[]>([]);
    const [linkedProjectNotes, setLinkedProjectNotes] = useState<NoteOption[]>([]);
    const [notesSearchInput, setNotesSearchInput] = useState('');
    const [notesSearchOptions, setNotesSearchOptions] = useState<NoteOption[]>([]);
    const [notesSearchLoading, setNotesSearchLoading] = useState(false);
    const [showLinkedNotesOnly, setShowLinkedNotesOnly] = useState(false);
    const [pathRows, setPathRows] = useState<FilePathEntry[]>([{ label: '', path: '' }]);
    const [linkedProjectTasks, setLinkedProjectTasks] = useState<Task[]>([]);
    const [taskSearchInput, setTaskSearchInput] = useState<string>('');
    const [taskSearchOptions, setTaskSearchOptions] = useState<Task[]>([]);
    const [taskSearchLoading, setTaskSearchLoading] = useState<boolean>(false);
    const [showLinkedTodosOnly, setShowLinkedTodosOnly] = useState<boolean>(false);
    const [showLinkedAssetsOnly, setShowLinkedAssetsOnly] = useState<boolean>(false);
    const [jsonError, setJsonError] = useState<string>('');
    const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'pending' | 'saving' | 'saved' | 'error'>('idle');
    const [showSaveEffect, setShowSaveEffect] = useState(false);
    const [saveEffectNonce, setSaveEffectNonce] = useState(0);
    const saveEffectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [isPersisting, setIsPersisting] = useState(false);
    const persistInFlightRef = useRef(false);
    const pendingAutosaveRef = useRef(false);
    const inFlightSnapshotRef = useRef<string | null>(null);
    /** Project id the in-flight persist targets ('' = create). */
    const savingProjectIdRef = useRef<string | null>(null);
    const persistProjectRef = useRef<(options: { isAutoSave: boolean; showSuccessToast: boolean }) => Promise<boolean>>(
        async () => false,
    );
    const flushPendingAutosaveRef = useRef<() => Promise<void>>(async () => {});
    const autoSaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const autoSaveResetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const taskSearchTimeoutRef = useRef<number | null>(null);
    const notesSearchTimeoutRef = useRef<number | null>(null);
    const lastAutoSavedSnapshotRef = useRef<string>('');
    const lastSavedRelationIdsRef = useRef<{
        task_ids: string[];
        note_ids: string[];
        asset_ids: string[];
    }>({ task_ids: [], note_ids: [], asset_ids: [] });
    const lastAppliedServerUpdatedAtRef = useRef(0);
    const lastLocalSaveAtRef = useRef(0);
    const pendingRemoteProjectRef = useRef<ProjectItem | null>(null);
    const pendingRemoteUpdatedAtRef = useRef(0);
    const handledProjectQueryRef = useRef<string | null>(null);
    const selectedProjectIdRef = useRef('');
    const todosSectionRef = useRef<HTMLDivElement | null>(null);
    const assetsSectionRef = useRef<HTMLDivElement | null>(null);

    selectedProjectIdRef.current = selectedProjectId;

    const projectsQuery = useQuery({
        queryKey,
        queryFn: () => projectsService.getCatalog(),
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
    });

    const filteredProjectsList = useMemo(() => {
        const term = projectListSearch.trim().toLowerCase();
        const all = projectsQuery.data ?? [];
        const byTag = projectTagFilter
            ? all.filter((project) => (project.tags ?? []).some((tag) => tag.name === projectTagFilter))
            : all;
        const filtered = !term
            ? [...byTag]
            : byTag.filter((project) => projectListHaystack(project).includes(term));

        return filtered.sort((a, b) => {
            const aMs = Date.parse(a.updated_at || '') || 0;
            const bMs = Date.parse(b.updated_at || '') || 0;
            return bMs - aMs;
        });
    }, [projectsQuery.data, projectListSearch, projectTagFilter]);

    const projectTagOptions = useMemo(() => {
        const names = new Set<string>();
        for (const project of projectsQuery.data ?? []) {
            for (const tag of project.tags ?? []) {
                names.add(tag.name);
            }
        }
        return Array.from(names).sort((a, b) => a.localeCompare(b));
    }, [projectsQuery.data]);

    const selectedProjectTags = useMemo(
        () => ((projectsQuery.data ?? []).find((project) => project.id === selectedProjectId)?.tags ?? []).map((tag) => tag.name),
        [projectsQuery.data, selectedProjectId],
    );

    /** Tags are saved immediately (partial PATCH), independent of the project autosave. */
    const saveProjectTags = async (names: string[]) => {
        const id = selectedProjectId.trim();
        if (!id) {
            return;
        }
        setTagsSaving(true);
        try {
            const saved = await projectsService.setTags(id, names);
            queryClient.setQueryData<ProjectItem[]>(queryKey, (current) =>
                current?.map((project) => (project.id === saved.id ? { ...project, tags: saved.tags } : project)),
            );
            await queryClient.invalidateQueries({ queryKey: ['tags'] });
        } catch {
            enqueueSnackbar(t('tags.saveError'), { variant: 'error' });
        } finally {
            setTagsSaving(false);
        }
    };

    const assetsQuery = useQuery({
        queryKey: ['external-assets-catalog'],
        queryFn: () => externalAssetService.getCatalog(),
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
    });

    const tasksQuery = useQuery({
        queryKey: ['tasks-for-projects'],
        queryFn: async () => {
            const data = await taskService.getAll(1, 500);
            return data.tasks ?? [];
        },
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
    });

    useFocusSyncToast({
        dataUpdatedAt: Math.max(
            projectsQuery.dataUpdatedAt ?? 0,
            assetsQuery.dataUpdatedAt ?? 0,
            tasksQuery.dataUpdatedAt ?? 0,
        ),
        isRefetching: projectsQuery.isRefetching || assetsQuery.isRefetching || tasksQuery.isRefetching,
        message: 'Projects data synced from another window',
    });

    useEffect(() => {
        if (selectedProjectId) {
            const projectName = (form.name ?? '').trim();
            if (projectName) {
                document.title = projectName;
                return;
            }
        }
        document.title = 'Projects';
    }, [selectedProjectId, form.name]);

    const selectedAssets = useMemo(() => {
        const byId = new Set(selectedAssetIds);
        return (assetsQuery.data ?? []).filter((asset) => byId.has(asset.id));
    }, [assetsQuery.data, selectedAssetIds]);

    useEffect(() => {
        const term = taskSearchInput.trim();
        if (!term) {
            setTaskSearchOptions([]);
            setTaskSearchLoading(false);
            return;
        }

        if (taskSearchTimeoutRef.current) {
            clearTimeout(taskSearchTimeoutRef.current);
        }

        taskSearchTimeoutRef.current = window.setTimeout(async () => {
            try {
                setTaskSearchLoading(true);
                const filters = `filters[name][$contains]=${encodeURIComponent(term)}`;
                const data = await taskService.getAll(1, 20, filters);
                setTaskSearchOptions(data.tasks ?? []);
            } catch {
                setTaskSearchOptions([]);
            } finally {
                setTaskSearchLoading(false);
            }
        }, 300);

        return () => {
            if (taskSearchTimeoutRef.current) {
                clearTimeout(taskSearchTimeoutRef.current);
            }
        };
    }, [taskSearchInput]);

    // Merge top list, project-linked tasks and search results.
    const tasksOptions = useMemo(() => {
        const merged = [...(tasksQuery.data ?? [])];
        const existingIds = new Set(merged.map((t) => t.id));
        for (const t of linkedProjectTasks) {
            if (!existingIds.has(t.id)) {
                merged.push(t);
                existingIds.add(t.id);
            }
        }
        for (const t of taskSearchOptions) {
            if (!existingIds.has(t.id)) {
                merged.push(t);
                existingIds.add(t.id);
            }
        }
        return merged;
    }, [tasksQuery.data, linkedProjectTasks, taskSearchOptions]);

    const selectedTasks = useMemo(() => {
        const byId = new Set(selectedTaskIds);
        return tasksOptions.filter((task) => byId.has(task.id));
    }, [tasksOptions, selectedTaskIds]);

    const statusOptions = useMemo(() => {
        const fromCatalog = (projectsQuery.data ?? [])
            .map((project) => (project.status ?? '').trim())
            .filter(Boolean);
        return Array.from(new Set([...PROJECT_STATUS_SUGGESTIONS, ...fromCatalog])).sort((a, b) =>
            a.localeCompare(b, undefined, { sensitivity: 'base' }),
        );
    }, [projectsQuery.data]);

    useEffect(() => {
        const term = notesSearchInput.trim();
        if (!term) {
            setNotesSearchOptions([]);
            setNotesSearchLoading(false);
            return;
        }

        if (notesSearchTimeoutRef.current) {
            clearTimeout(notesSearchTimeoutRef.current);
        }

        notesSearchTimeoutRef.current = window.setTimeout(async () => {
            try {
                setNotesSearchLoading(true);
                const filters = `filters[name][$contains]=${encodeURIComponent(term)}`;
                const data = await getNotes({ filters, per_page: 20 });
                setNotesSearchOptions(
                    dedupeNoteOptions(
                        (data.notes ?? []).map((note) => ({ id: String(note.id), name: note.name ?? '' })),
                    ),
                );
            } catch {
                setNotesSearchOptions([]);
            } finally {
                setNotesSearchLoading(false);
            }
        }, 300);

        return () => {
            if (notesSearchTimeoutRef.current) {
                clearTimeout(notesSearchTimeoutRef.current);
            }
        };
    }, [notesSearchInput]);

    const notesOptions = useMemo(() => {
        const merged: NoteOption[] = dedupeNoteOptions(linkedProjectNotes);
        const existingIds = new Set(merged.map((note) => String(note.id)));
        for (const note of dedupeNoteOptions(notesSearchOptions)) {
            if (!existingIds.has(String(note.id))) {
                merged.push(note);
                existingIds.add(String(note.id));
            }
        }
        return merged;
    }, [linkedProjectNotes, notesSearchOptions]);

    const selectedNotes = useMemo(() => {
        const byId = new Set(selectedNoteIds.map((id) => String(id)));
        return notesOptions.filter((note) => byId.has(String(note.id)));
    }, [notesOptions, selectedNoteIds]);

    const filteredNotesOptions = useMemo(() => {
        const term = notesSearchInput.trim();
        if (term) {
            return dedupeNoteOptions(notesSearchOptions);
        }
        if (!showLinkedNotesOnly) {
            return notesOptions;
        }
        const ids = new Set(selectedNoteIds.map((id) => String(id)));
        return notesOptions.filter((note) => ids.has(String(note.id)));
    }, [notesOptions, notesSearchInput, notesSearchOptions, selectedNoteIds, showLinkedNotesOnly]);

    const updatePathRows = useCallback((rows: FilePathEntry[]) => {
        const normalized = rows.length ? rows : [{ label: '', path: '' }];
        const paths = toFilePathPayloadFromEntries(normalized);
        setPathRows(normalized);
        setForm((prev) => {
            const nextForm = { ...prev, paths };
            currentStateRef.current = {
                ...currentStateRef.current,
                form: nextForm,
                pathRows: normalized,
            };
            return nextForm;
        });
    }, []);

    const copyValueToClipboard = useCallback(async (value?: string | null) => {
        const text = (value ?? '').trim();
        if (!text) {
            return;
        }
        try {
            await navigator.clipboard.writeText(text);
            enqueueSnackbar('Copied to clipboard', { variant: 'success' });
        } catch {
            enqueueSnackbar('Failed to copy', { variant: 'error' });
        }
    }, [enqueueSnackbar]);

    const filteredTasksOptions = useMemo(() => {
        if (!showLinkedTodosOnly) {
            return tasksOptions;
        }
        const ids = new Set(selectedTaskIds);
        return tasksOptions.filter((task) => ids.has(task.id));
    }, [showLinkedTodosOnly, tasksOptions, selectedTaskIds]);

    const filteredAssetsOptions = useMemo(() => {
        const allAssets = assetsQuery.data ?? [];
        if (!showLinkedAssetsOnly) {
            return allAssets;
        }
        const ids = new Set(selectedAssetIds);
        return allAssets.filter((asset) => ids.has(asset.id));
    }, [assetsQuery.data, selectedAssetIds, showLinkedAssetsOnly]);

    const selectedProjectIdFromQuery = useMemo(() => {
        const params = new URLSearchParams(location.search);
        return (params.get('projectId') ?? '').trim();
    }, [location.search]);

    const syncProjectIdToUrl = useCallback(
        (projectId: string) => {
            const params = new URLSearchParams(location.search);
            params.delete('taskId');
            params.delete('newTask');

            const normalizedProjectId = projectId.trim();
            if (normalizedProjectId) {
                params.set('projectId', normalizedProjectId);
            } else {
                params.delete('projectId');
            }

            const nextSearch = params.toString();
            const nextPath = `${location.pathname}${nextSearch ? `?${nextSearch}` : ''}`;
            const currentPath = `${location.pathname}${location.search}`;

            handledProjectQueryRef.current = normalizedProjectId || null;

            if (nextPath !== currentPath) {
                navigate(nextPath, { replace: true });
            }
        },
        [location.pathname, location.search, navigate],
    );

    const tasksSummaryHref = useMemo(() => (
        selectedProjectId ? `/tasks?projectId=${encodeURIComponent(selectedProjectId)}` : '/tasks'
    ), [selectedProjectId]);

    const assetsSummaryHref = useMemo(() => (
        selectedProjectId ? `/external-assets?projectId=${encodeURIComponent(selectedProjectId)}` : '/external-assets'
    ), [selectedProjectId]);

    const handleTasksChangedInProjectView = useCallback(async () => {
        await queryClient.invalidateQueries({ queryKey: ['tasks-for-projects'] });
        await queryClient.invalidateQueries({ queryKey });
        if (selectedProjectId) {
            try {
                const tasks = await projectsService.getProjectTasks(selectedProjectId);
                setLinkedProjectTasks(tasks);
            } catch {
                // Keep existing task chips visible even if refresh fails.
            }
        }
    }, [queryClient, selectedProjectId]);

    const loadProjectToForm = useCallback((project: ProjectItem | null) => {
        if (!project) {
            setForm(emptyForm);
            setSelectedAssetIds([]);
            setSelectedTaskIds([]);
            setSelectedNoteIds([]);
            setLinkedProjectNotes([]);
            setLinkedProjectTasks([]);
            setTaskSearchInput('');
            setTaskSearchOptions([]);
            setNotesSearchInput('');
            setNotesSearchOptions([]);
            setShowLinkedTodosOnly(false);
            setShowLinkedAssetsOnly(false);
            setShowLinkedNotesOnly(false);
            setPathRows([{ label: '', path: '' }]);
            setJsonError('');
            lastAutoSavedSnapshotRef.current = buildProjectSnapshot(emptyForm, [], [], [], [{ label: '', path: '' }]);
            lastSavedRelationIdsRef.current = { task_ids: [], note_ids: [], asset_ids: [] };
            lastAppliedServerUpdatedAtRef.current = 0;
            pendingRemoteProjectRef.current = null;
            pendingRemoteUpdatedAtRef.current = 0;
            return;
        }

        const parsedPathRows = parseFilePathEntries(project.paths ?? '');
        const resolvedPathRows = parsedPathRows.length ? parsedPathRows : [{ label: '', path: '' }];
        const nextForm: ProjectFormState = {
            name: project.name ?? '',
            status: project.status ?? '',
            description: normalizeTxtEditorHtml(project.description ?? ''),
            paths: project.paths ?? '',
            notes: normalizeTxtEditorHtml(project.notes ?? ''),
            assets_json: project.assets_json ?? '',
        };
        setForm(nextForm);
        setPathRows(resolvedPathRows);
        const parsedAssetIds = parseAssetIds(nextForm.assets_json);
        const resolvedAssetIds = (project.asset_ids ?? []).length ? (project.asset_ids ?? []) : parsedAssetIds;
        const resolvedTaskIds = project.task_ids ?? [];
        const resolvedNoteIds = project.note_ids ?? [];
        setSelectedAssetIds(resolvedAssetIds);
        setSelectedTaskIds(resolvedTaskIds);
        setSelectedNoteIds(resolvedNoteIds);
        setLinkedProjectNotes(
            dedupeNoteOptions(
                (project.linked_notes ?? []).map((note) => ({ id: String(note.id), name: note.name ?? '' })),
            ),
        );
        setLinkedProjectTasks([]);
        setTaskSearchInput('');
        setTaskSearchOptions([]);
        setNotesSearchInput('');
        setNotesSearchOptions([]);
        setShowLinkedTodosOnly(false);
        setShowLinkedAssetsOnly(false);
        setShowLinkedNotesOnly(false);
        setJsonError('');
        lastAutoSavedSnapshotRef.current = buildProjectSnapshot(
            nextForm,
            resolvedTaskIds,
            resolvedNoteIds,
            resolvedAssetIds,
            resolvedPathRows,
        );
        lastSavedRelationIdsRef.current = {
            task_ids: sortStableIds(resolvedTaskIds),
            note_ids: sortStableIds(resolvedNoteIds),
            asset_ids: sortStableIds(resolvedAssetIds),
        };
        lastAppliedServerUpdatedAtRef.current = parseProjectUpdatedAtMs(project.updated_at);
        pendingRemoteProjectRef.current = null;
        pendingRemoteUpdatedAtRef.current = 0;

        if (project.id && resolvedTaskIds.length > 0) {
            projectsService.getProjectTasks(project.id).then((tasks) => {
                setLinkedProjectTasks(tasks);
            }).catch(() => {});
        }
    }, []);

    const selectProject = useCallback(
        async (project: ProjectItem | null) => {
            // Debounced path edits are otherwise cancelled when the form is replaced.
            await flushPendingAutosaveRef.current();

            if (project) {
                setSelectedProjectId(project.id);
                loadProjectToForm(project);
                syncProjectIdToUrl(project.id);
                return;
            }

            setSelectedProjectId('');
            loadProjectToForm(null);
            syncProjectIdToUrl('');
        },
        [loadProjectToForm, syncProjectIdToUrl],
    );

    useEffect(() => {
        if (!selectedProjectIdFromQuery) {
            handledProjectQueryRef.current = null;
            return;
        }

        if (handledProjectQueryRef.current === selectedProjectIdFromQuery) {
            return;
        }

        const targetProject = (projectsQuery.data ?? []).find((project) => project.id === selectedProjectIdFromQuery);
        if (!targetProject) {
            return;
        }

        let cancelled = false;
        void (async () => {
            await flushPendingAutosaveRef.current();
            if (cancelled) {
                return;
            }
            if (handledProjectQueryRef.current === selectedProjectIdFromQuery) {
                return;
            }
            setSelectedProjectId(targetProject.id);
            loadProjectToForm(targetProject);
            handledProjectQueryRef.current = selectedProjectIdFromQuery;
        })();

        return () => {
            cancelled = true;
        };
    }, [loadProjectToForm, projectsQuery.data, selectedProjectIdFromQuery]);

    // Keep a ref to current form/ids so the projectsQuery effect below can read
    // latest values without being added to its dependency array.
    const currentStateRef = useRef({ form, selectedTaskIds, selectedNoteIds, selectedAssetIds, pathRows });

    const flushCurrentStateRef = useCallback(() => {
        currentStateRef.current = {
            form,
            selectedTaskIds,
            selectedNoteIds,
            selectedAssetIds,
            pathRows,
        };
    }, [form, selectedAssetIds, selectedNoteIds, selectedTaskIds, pathRows]);

    useLayoutEffect(() => {
        flushCurrentStateRef();
    }, [flushCurrentStateRef]);

    // When projects catalog refreshes (focus / another window), apply remote fields
    // including Notes memo — but only if local form is clean and server is newer.
    useEffect(() => {
        if (!selectedProjectId || !projectsQuery.data) {
            return;
        }
        if (persistInFlightRef.current) {
            return;
        }
        if (Date.now() - lastLocalSaveAtRef.current < 3000) {
            return;
        }
        const freshProject = projectsQuery.data.find((p) => p.id === selectedProjectId);
        if (!freshProject) {
            return;
        }

        const freshMs = parseProjectUpdatedAtMs(freshProject.updated_at);
        if (freshMs <= lastAppliedServerUpdatedAtRef.current) {
            return;
        }

        const {
            form: f,
            selectedTaskIds: tIds,
            selectedNoteIds: nIds,
            selectedAssetIds: aIds,
            pathRows: pRows,
        } = currentStateRef.current;
        const currentSnapshot = buildProjectSnapshot(f, tIds, nIds, aIds, pRows);
        const isDirty = currentSnapshot !== lastAutoSavedSnapshotRef.current;

        if (isDirty) {
            if (pendingRemoteUpdatedAtRef.current === freshMs) {
                return;
            }
            pendingRemoteUpdatedAtRef.current = freshMs;
            pendingRemoteProjectRef.current = freshProject;
            enqueueSnackbar('Project updated elsewhere (not applied).', {
                variant: 'info',
                action: (snackbarId) => (
                    <Button
                        color="inherit"
                        size="small"
                        onClick={() => {
                            const pending = pendingRemoteProjectRef.current;
                            if (pending) {
                                loadProjectToForm(pending);
                            }
                            closeSnackbar(snackbarId);
                        }}
                    >
                        Reload
                    </Button>
                ),
            });
            return;
        }

        loadProjectToForm(freshProject);
    }, [closeSnackbar, enqueueSnackbar, loadProjectToForm, projectsQuery.data, selectedProjectId]);

    const buildProjectPayloadFromRef = useCallback((): UpsertProjectPayload | null => {
        const {
            form: stateForm,
            pathRows: statePathRows,
            selectedTaskIds: stateTaskIds,
            selectedNoteIds: stateNoteIds,
            selectedAssetIds: stateAssetIds,
        } = currentStateRef.current;

        const name = stateForm.name.trim();
        if (!name) {
            return null;
        }

        return {
            name,
            status: stateForm.status.trim(),
            description: normalizeTxtEditorHtml(stateForm.description ?? ''),
            paths: toFilePathPayloadFromEntries(statePathRows),
            notes: normalizeTxtEditorHtml(stateForm.notes ?? ''),
            assets_json: stateForm.assets_json,
            task_ids: sortStableIds(stateTaskIds),
            note_ids: sortStableIds(stateNoteIds),
            asset_ids: sortStableIds(stateAssetIds),
        };
    }, []);

    /** Drop unchanged relation arrays so API skips pivot sync (paths-only edits stay fast). */
    const toUpdateRequestPayload = useCallback((payload: UpsertProjectPayload): UpsertProjectPayload => {
        const next: UpsertProjectPayload = { ...payload };
        const last = lastSavedRelationIdsRef.current;
        if (sameIdList(payload.task_ids ?? [], last.task_ids)) {
            delete next.task_ids;
        }
        if (sameIdList(payload.note_ids ?? [], last.note_ids)) {
            delete next.note_ids;
        }
        if (sameIdList(payload.asset_ids ?? [], last.asset_ids)) {
            delete next.asset_ids;
            // API also syncs assets when assets_json is present — omit if links unchanged.
            delete next.assets_json;
        }
        return next;
    }, []);

    const triggerSaveCelebrations = useCallback(() => {
        if (!areSaveCelebrationsEnabled()) {
            return;
        }

        setSaveEffectNonce((prev) => prev + 1);
        setShowSaveEffect(true);
        if (saveEffectTimerRef.current) {
            clearTimeout(saveEffectTimerRef.current);
        }
        // Clear exactly when the keyframe ends — no extra hold that re-shows opacity.
        saveEffectTimerRef.current = setTimeout(() => {
            setShowSaveEffect(false);
            saveEffectTimerRef.current = null;
        }, SAVE_CELEBRATION_FLASH_MS);

        try {
            runConfettiBurst();
            playSaveSound();
        } catch (err) {
            console.error('project save celebration error', err);
        }
    }, []);

    useEffect(() => () => {
        if (saveEffectTimerRef.current) {
            clearTimeout(saveEffectTimerRef.current);
        }
    }, []);

    const persistProject = useCallback(
        async (options: { isAutoSave: boolean; showSuccessToast: boolean }): Promise<boolean> => {
            flushCurrentStateRef();
            const payload = buildProjectPayloadFromRef();
            if (!payload) {
                if (!options.isAutoSave) {
                    enqueueSnackbar('Project name is required.', { variant: 'warning' });
                }
                return false;
            }

            const payloadSnapshot = buildProjectSnapshotFromPayload(payload);
            if (payloadSnapshot === lastAutoSavedSnapshotRef.current) {
                if (options.isAutoSave) {
                    setAutoSaveStatus('idle');
                }
                return true;
            }

            if (persistInFlightRef.current) {
                if (payloadSnapshot !== inFlightSnapshotRef.current) {
                    pendingAutosaveRef.current = true;
                }
                return false;
            }

            // Capture target id at start — user may switch projects while this request is in flight.
            const savingProjectId = selectedProjectIdRef.current.trim();
            persistInFlightRef.current = true;
            savingProjectIdRef.current = savingProjectId;
            inFlightSnapshotRef.current = payloadSnapshot;
            const previousSnapshot = lastAutoSavedSnapshotRef.current;
            // Mark clean immediately so post-save re-renders / normalize cannot schedule a twin save.
            lastAutoSavedSnapshotRef.current = payloadSnapshot;
            setIsPersisting(true);

            const isStillEditingSavedProject = (savedId: string): boolean => {
                const currentId = selectedProjectIdRef.current.trim();
                if (savingProjectId) {
                    return currentId === savingProjectId;
                }
                // Create: still on the blank "new" form, or already pointed at the created id.
                return currentId === '' || currentId === savedId;
            };

            try {
                if (options.isAutoSave) {
                    setAutoSaveStatus('saving');
                }

                const saved = savingProjectId
                    ? await projectsService.update(savingProjectId, toUpdateRequestPayload(payload))
                    : await projectsService.create(payload);

                // Always merge into catalog cache for the saved project.
                queryClient.setQueryData<ProjectItem[]>(queryKey, (current) => {
                    if (!current) {
                        return current;
                    }
                    const index = current.findIndex((project) => project.id === saved.id);
                    if (index === -1) {
                        return [saved, ...current];
                    }
                    const next = [...current];
                    next[index] = { ...next[index], ...saved };
                    return next;
                });

                if (!options.isAutoSave) {
                    await queryClient.invalidateQueries({ queryKey });
                }

                const stillHere = isStillEditingSavedProject(saved.id);
                if (!stillHere) {
                    // Form already belongs to another project — do not clobber its baseline/UI.
                    if (options.isAutoSave) {
                        setAutoSaveStatus('idle');
                    }
                    return true;
                }

                lastAppliedServerUpdatedAtRef.current = parseProjectUpdatedAtMs(saved.updated_at);
                lastLocalSaveAtRef.current = Date.now();
                lastSavedRelationIdsRef.current = {
                    task_ids: sortStableIds(payload.task_ids ?? []),
                    note_ids: sortStableIds(payload.note_ids ?? []),
                    asset_ids: sortStableIds(payload.asset_ids ?? []),
                };

                setSelectedProjectId(saved.id);
                syncProjectIdToUrl(saved.id);

                if (options.isAutoSave) {
                    lastAutoSavedSnapshotRef.current = payloadSnapshot;
                    const {
                        selectedTaskIds: savedTaskIds,
                        selectedNoteIds: savedNoteIds,
                        selectedAssetIds: savedAssetIds,
                    } = currentStateRef.current;
                    const resolvedTaskIds = saved.task_ids ?? savedTaskIds;
                    const resolvedNoteIds = saved.note_ids ?? savedNoteIds;
                    const resolvedAssetIds = saved.asset_ids ?? savedAssetIds;
                    if (!sameIdList(resolvedTaskIds, savedTaskIds)) {
                        setSelectedTaskIds(resolvedTaskIds);
                    }
                    if (!sameIdList(resolvedNoteIds, savedNoteIds)) {
                        setSelectedNoteIds(resolvedNoteIds);
                    }
                    if (!sameIdList(resolvedAssetIds, savedAssetIds)) {
                        setSelectedAssetIds(resolvedAssetIds);
                    }
                    setLinkedProjectNotes(
                        dedupeNoteOptions(
                            (saved.linked_notes ?? []).map((note) => ({
                                id: String(note.id),
                                name: note.name ?? '',
                            })),
                        ),
                    );
                    // Avoid blocking autosave UI on a full tasks refetch for path-only edits.
                    if ((saved.task_ids ?? []).length > 0 && !sameIdList(resolvedTaskIds, savedTaskIds)) {
                        projectsService.getProjectTasks(saved.id).then((tasks) => {
                            setLinkedProjectTasks(tasks);
                        }).catch(() => {});
                    }
                } else {
                    loadProjectToForm(saved);
                }

                triggerSaveCelebrations();

                if (options.showSuccessToast) {
                    enqueueSnackbar(`Saved project: ${saved.name}`, { variant: 'success' });
                }

                if (options.isAutoSave) {
                    setAutoSaveStatus('saved');
                    if (autoSaveResetTimerRef.current) {
                        clearTimeout(autoSaveResetTimerRef.current);
                    }
                    autoSaveResetTimerRef.current = setTimeout(() => setAutoSaveStatus('idle'), 1200);
                }

                return true;
            } catch (error) {
                if (selectedProjectIdRef.current.trim() === savingProjectId) {
                    lastAutoSavedSnapshotRef.current = previousSnapshot;
                }
                if (options.isAutoSave) {
                    setAutoSaveStatus('error');
                } else {
                    enqueueSnackbar(error instanceof Error ? error.message : 'Failed to save project', {
                        variant: 'error',
                    });
                }
                return false;
            } finally {
                persistInFlightRef.current = false;
                savingProjectIdRef.current = null;
                inFlightSnapshotRef.current = null;
                setIsPersisting(false);

                if (pendingAutosaveRef.current) {
                    pendingAutosaveRef.current = false;
                    // Only chain another save if we are still on the project that queued it.
                    const currentId = selectedProjectIdRef.current.trim();
                    if (currentId === savingProjectId || (!savingProjectId && currentId === '')) {
                        flushCurrentStateRef();
                        const pendingSnapshot = buildProjectSnapshot(
                            currentStateRef.current.form,
                            currentStateRef.current.selectedTaskIds,
                            currentStateRef.current.selectedNoteIds,
                            currentStateRef.current.selectedAssetIds,
                            currentStateRef.current.pathRows,
                        );
                        if (pendingSnapshot !== lastAutoSavedSnapshotRef.current) {
                            void persistProjectRef.current({ isAutoSave: true, showSuccessToast: false });
                        }
                    }
                }
            }
        },
        [
            buildProjectPayloadFromRef,
            enqueueSnackbar,
            flushCurrentStateRef,
            loadProjectToForm,
            queryClient,
            syncProjectIdToUrl,
            toUpdateRequestPayload,
            triggerSaveCelebrations,
        ],
    );

    persistProjectRef.current = persistProject;

    const waitForPersistIdle = useCallback(async () => {
        for (let attempt = 0; attempt < 120; attempt += 1) {
            if (!persistInFlightRef.current) {
                return;
            }
            await new Promise<void>((resolve) => {
                window.setTimeout(resolve, 50);
            });
        }
    }, []);

    const flushPendingAutosave = useCallback(async () => {
        if (autoSaveTimeoutRef.current) {
            clearTimeout(autoSaveTimeoutRef.current);
            autoSaveTimeoutRef.current = null;
        }

        await waitForPersistIdle();
        flushCurrentStateRef();

        const state = currentStateRef.current;
        if (!state.form.name.trim()) {
            return;
        }

        const snapshot = buildProjectSnapshot(
            state.form,
            state.selectedTaskIds,
            state.selectedNoteIds,
            state.selectedAssetIds,
            state.pathRows,
        );
        if (snapshot === lastAutoSavedSnapshotRef.current) {
            return;
        }

        await persistProjectRef.current({ isAutoSave: true, showSuccessToast: false });
        await waitForPersistIdle();
    }, [flushCurrentStateRef, waitForPersistIdle]);

    flushPendingAutosaveRef.current = flushPendingAutosave;

    // Flush pending path/form edits when leaving the page or hiding the tab.
    useEffect(() => {
        const onPageHide = () => {
            void flushPendingAutosaveRef.current();
        };
        const onVisibilityChange = () => {
            if (document.visibilityState === 'hidden') {
                void flushPendingAutosaveRef.current();
            }
        };
        window.addEventListener('pagehide', onPageHide);
        document.addEventListener('visibilitychange', onVisibilityChange);
        return () => {
            window.removeEventListener('pagehide', onPageHide);
            document.removeEventListener('visibilitychange', onVisibilityChange);
            void flushPendingAutosaveRef.current();
        };
    }, []);

    const deleteMutation = useMutation({
        mutationFn: async () => {
            if (!selectedProjectId) {
                throw new Error('Select a project first.');
            }
            await projectsService.delete(selectedProjectId);
        },
        onSuccess: async () => {
            enqueueSnackbar('Project deleted', { variant: 'success' });
            await queryClient.invalidateQueries({ queryKey });
            selectProject(null);
        },
        onError: (error) => {
            enqueueSnackbar(error instanceof Error ? error.message : 'Failed to delete project', { variant: 'error' });
        },
    });

    // Debounced autosave: create (no selected id) or update, same pattern as External Assets
    useEffect(() => {
        if (!form.name.trim()) {
            return;
        }

        const snapshot = buildProjectSnapshot(form, selectedTaskIds, selectedNoteIds, selectedAssetIds, pathRows);
        if (snapshot === lastAutoSavedSnapshotRef.current) {
            return;
        }

        if (persistInFlightRef.current) {
            if (snapshot !== inFlightSnapshotRef.current) {
                pendingAutosaveRef.current = true;
            }
            return;
        }

        if (autoSaveTimeoutRef.current) {
            clearTimeout(autoSaveTimeoutRef.current);
        }

        setAutoSaveStatus('pending');
        autoSaveTimeoutRef.current = setTimeout(() => {
            void persistProjectRef.current({ isAutoSave: true, showSuccessToast: false });
        }, AUTO_SAVE_DEBOUNCE_MS);

        return () => {
            if (autoSaveTimeoutRef.current) {
                clearTimeout(autoSaveTimeoutRef.current);
            }
        };
    }, [form, pathRows, selectedTaskIds, selectedNoteIds, selectedAssetIds]);

    const handleNotesMemoChange = useCallback((notes: string) => {
        const normalized = normalizeTxtEditorHtml(notes ?? '');
        setForm((prev) => (prev.notes === normalized ? prev : { ...prev, notes: normalized }));
    }, []);

    const handleDescriptionChange = useCallback((description: string) => {
        const normalized = normalizeTxtEditorHtml(description ?? '');
        setForm((prev) =>
            prev.description === normalized ? prev : { ...prev, description: normalized },
        );
    }, []);

    return (
        <Box sx={{ p: 3 }}>
            <SaveCelebrationOverlay active={showSaveEffect} nonce={saveEffectNonce} />
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Stack direction="row" spacing={2} alignItems="center">
                    <Typography variant="h4">Projects</Typography>
                    {form.name.trim() && (
                        <Typography
                            variant="caption"
                            sx={{
                                px: 1,
                                py: 0.5,
                                borderRadius: 0.5,
                                fontWeight: 600,
                                color: autoSaveStatus === 'error' ? 'error.dark' : 'text.primary',
                                bgcolor:
                                    autoSaveStatus === 'pending'
                                        ? 'warning.light'
                                        : autoSaveStatus === 'saving'
                                            ? 'info.light'
                                            : autoSaveStatus === 'saved'
                                                ? 'success.light'
                                                : autoSaveStatus === 'error'
                                                    ? 'error.light'
                                                    : 'action.hover',
                            }}
                        >
                            {autoSaveStatus === 'pending' && 'Autosave pending...'}
                            {autoSaveStatus === 'saving' && 'Autosaving...'}
                            {autoSaveStatus === 'saved' && 'Saved ✓'}
                            {autoSaveStatus === 'error' && 'Autosave failed'}
                            {autoSaveStatus === 'idle' && 'Autosave ready'}
                        </Typography>
                    )}
                </Stack>
                <Stack direction="row" spacing={1}>
                    <Button
                        variant="outlined"
                        onClick={() => {
                            void selectProject(null);
                        }}
                    >
                        New
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => {
                            if (autoSaveTimeoutRef.current) {
                                clearTimeout(autoSaveTimeoutRef.current);
                                autoSaveTimeoutRef.current = null;
                            }
                            void persistProject({ isAutoSave: false, showSuccessToast: true });
                        }}
                        disabled={isPersisting}
                    >
                        Save
                    </Button>
                    <Button
                        color="error"
                        variant="outlined"
                        onClick={() => deleteMutation.mutate()}
                        disabled={!selectedProjectId || deleteMutation.isPending}
                    >
                        Delete
                    </Button>
                </Stack>
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <Paper
                    variant="outlined"
                    sx={{
                        width: { xs: '100%', md: 320 },
                        maxHeight: 680,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    }}
                >
                    <Box
                        sx={{
                            p: 1.5,
                            flexShrink: 0,
                            borderBottom: 1,
                            borderColor: 'divider',
                        }}
                    >
                        <TextField
                            size="small"
                            fullWidth
                            placeholder="Search name, status, notes, paths…"
                            value={projectListSearch}
                            onChange={(event) => setProjectListSearch(event.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" color="action" />
                                    </InputAdornment>
                                ),
                                endAdornment: projectListSearch.trim() ? (
                                    <InputAdornment position="end">
                                        <IconButton
                                            size="small"
                                            edge="end"
                                            aria-label="Clear project search"
                                            onClick={() => setProjectListSearch('')}
                                        >
                                            <ClearIcon fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                ) : undefined,
                            }}
                        />
                        {projectTagOptions.length > 0 && (
                            <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                <Chip
                                    size="small"
                                    label={t('tags.all')}
                                    color={projectTagFilter === null ? 'primary' : 'default'}
                                    onClick={() => setProjectTagFilter(null)}
                                />
                                {projectTagOptions.map((name) => (
                                    <Chip
                                        key={name}
                                        size="small"
                                        label={name}
                                        color={projectTagFilter === name ? 'primary' : 'default'}
                                        variant={projectTagFilter === name ? 'filled' : 'outlined'}
                                        onClick={() => setProjectTagFilter(projectTagFilter === name ? null : name)}
                                    />
                                ))}
                            </Box>
                        )}
                        {(projectListSearch.trim() || projectTagFilter) && !projectsQuery.isLoading && (
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
                                {filteredProjectsList.length} of {(projectsQuery.data ?? []).length} projects
                            </Typography>
                        )}
                    </Box>
                    <Box sx={{ flex: 1, overflow: 'auto' }}>
                        {projectsQuery.isLoading ? (
                            <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
                                <CircularProgress size={22} />
                            </Box>
                        ) : filteredProjectsList.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                                {projectListSearch.trim()
                                    ? 'No projects match your search.'
                                    : 'No projects yet. Click New to create one.'}
                            </Typography>
                        ) : (
                            <List dense>
                                {filteredProjectsList.map((project) => (
                                    <ListItemButton
                                        key={project.id}
                                        selected={selectedProjectId === project.id}
                                        onClick={() => selectProject(project)}
                                    >
                                        <ListItemText
                                            primary={renderTextWithMarkers(project.name || '(Unnamed)', 'table')}
                                            secondary={(
                                                <>
                                                    {`${project.status || 'No status'} - ${project.updated_at || ''}`}
                                                    {(project.tags ?? []).length > 0 && (
                                                        <Box component="span" sx={{ display: 'block', mt: 0.5 }}>
                                                            <TagChips tags={project.tags} max={3} onClick={(name) => setProjectTagFilter(name)} />
                                                        </Box>
                                                    )}
                                                </>
                                            )}
                                            secondaryTypographyProps={{ component: 'div' }}
                                        />
                                    </ListItemButton>
                                ))}
                            </List>
                        )}
                    </Box>
                </Paper>

                <Paper variant="outlined" sx={{ flex: 1, p: 2 }}>
                    <Stack spacing={2}>
                        <MarkerHighlightTextField
                            label="Name"
                            required
                            value={form.name}
                            onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                            fullWidth
                        />
                        <Autocomplete
                            freeSolo
                            options={statusOptions}
                            value={form.status}
                            onChange={(_event, value) => setForm((prev) => ({ ...prev, status: value ?? '' }))}
                            onInputChange={(_event, value) => setForm((prev) => ({ ...prev, status: value }))}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Status"
                                    placeholder="Planning, Active, On hold…"
                                />
                            )}
                        />
                        <TagsInput
                            value={selectedProjectTags}
                            onChange={(names) => void saveProjectTags(names)}
                            suggestions={SUGGESTED_PROJECT_TAGS}
                            disabled={!selectedProjectId || tagsSaving}
                        />
                        <RichHtmlEditorField
                            label="Description"
                            value={form.description}
                            onChange={handleDescriptionChange}
                            editorModeStorageKey="projects.description.editorMode"
                            showHtmlSourceStorageKey="projects.description.showHtmlSource"
                            paneHeight={200}
                            joditMinHeight={200}
                        />

                        <FilePathEntriesEditor
                            entries={pathRows}
                            onChange={updatePathRows}
                            onCopy={copyValueToClipboard}
                            onBlur={() => {
                                void flushPendingAutosaveRef.current();
                            }}
                        />

                        <Box>
                            <RichHtmlEditorField
                                label="Notes (memo)"
                                value={form.notes}
                                onChange={handleNotesMemoChange}
                                editorModeStorageKey="projects.memo.editorMode"
                                showHtmlSourceStorageKey="projects.memo.showHtmlSource"
                                paneHeight={280}
                                joditMinHeight={280}
                                helperText="Free-form project notes. Use the selector below to link Notes app entries."
                            />
                        </Box>

                        <Divider />

                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap>
                            <Typography variant="subtitle1">Notes selector</Typography>
                            <Button
                                size="small"
                                variant={showLinkedNotesOnly ? 'contained' : 'outlined'}
                                onClick={() => setShowLinkedNotesOnly((prev) => !prev)}
                            >
                                {showLinkedNotesOnly ? 'Showing linked only' : 'Show linked only'}
                            </Button>
                        </Stack>
                        <Autocomplete
                            multiple
                            options={filteredNotesOptions}
                            loading={notesSearchLoading}
                            value={selectedNotes}
                            inputValue={notesSearchInput}
                            onInputChange={(_event, value, reason) => {
                                if (reason !== 'input' && reason !== 'clear') {
                                    return;
                                }
                                setNotesSearchInput(value);
                            }}
                            onChange={(_event, value: NoteOption[]) => {
                                setSelectedNoteIds(value.map((note) => String(note.id)));
                            }}
                            isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                            getOptionKey={(option) => String(option.id)}
                            getOptionLabel={(option) => option.name || option.id}
                            filterOptions={(options) => options}
                            clearOnBlur={false}
                            autoSelect={false}
                            freeSolo={false}
                            renderTags={(value, getTagProps) =>
                                value.map((note, index) => (
                                    <Chip
                                        {...getTagProps({ index })}
                                        key={note.id}
                                        label={(
                                            <Link
                                                to={`/notes/${encodeURIComponent(note.id)}`}
                                                target="_blank"
                                                style={{ color: 'inherit', textDecoration: 'none' }}
                                            >
                                                {note.name || note.id}
                                            </Link>
                                        )}
                                    />
                                ))
                            }
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Select notes"
                                    placeholder="Start typing note name"
                                />
                            )}
                        />

                        <Divider />

                        <Box ref={todosSectionRef} sx={{ scrollMarginTop: 80 }}>
                            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap>
                                <Typography variant="subtitle1">Todos selector</Typography>
                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                    <Button
                                        size="small"
                                        variant="contained"
                                        disabled={!selectedProjectId}
                                        onClick={() => {
                                            if (!selectedProjectId) {
                                                enqueueSnackbar('Select a project first.', { variant: 'info' });
                                                return;
                                            }
                                            navigate(
                                                `/projects?projectId=${encodeURIComponent(selectedProjectId)}&newTask=1`,
                                            );
                                        }}
                                    >
                                        New task
                                    </Button>
                                    <Button
                                        size="small"
                                        variant={showLinkedTodosOnly ? 'contained' : 'outlined'}
                                        onClick={() => setShowLinkedTodosOnly((prev) => !prev)}
                                    >
                                        {showLinkedTodosOnly ? 'Showing linked only' : 'Show linked only'}
                                    </Button>
                                </Stack>
                            </Stack>
                        </Box>
                        <Autocomplete
                            multiple
                            options={filteredTasksOptions}
                            loading={tasksQuery.isLoading || taskSearchLoading}
                            value={selectedTasks}
                            inputValue={taskSearchInput}
                            onInputChange={(_event, value, reason) => {
                                if (reason !== 'input' && reason !== 'clear') {
                                    return;
                                }
                                setTaskSearchInput(value);
                            }}
                            onChange={(_event, value: Task[]) => {
                                setSelectedTaskIds(value.map((task) => task.id));
                            }}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            getOptionLabel={(option) => `${option.name}${option.priority?.label ? ` (${option.priority.label})` : ''}`}
                            filterOptions={(options) => options}
                            clearOnBlur={false}
                            autoSelect={false}
                            freeSolo={false}
                            renderTags={(value, getTagProps) =>
                                value.map((task, index) => (
                                    <Chip
                                        {...getTagProps({ index })}
                                        key={task.id}
                                        sx={{
                                            bgcolor: task.status ? '#dcfce7' : '#fee2e2',
                                            color: task.status ? '#14532d' : '#991b1b',
                                            '& .MuiChip-deleteIcon': {
                                                color: task.status ? '#166534' : '#b91c1c',
                                            },
                                            '& .MuiChip-deleteIcon:hover': {
                                                color: task.status ? '#14532d' : '#991b1b',
                                            },
                                        }}
                                        label={(
                                            <Link
                                                to={selectedProjectId
                                                    ? `/projects?projectId=${encodeURIComponent(selectedProjectId)}&taskId=${encodeURIComponent(task.id)}`
                                                    : `/projects?taskId=${encodeURIComponent(task.id)}`
                                                }
                                                style={{
                                                    color: 'inherit',
                                                    textDecoration: 'none',
                                                }}
                                                onClick={() => {
                                                    window.dispatchEvent(
                                                        new CustomEvent(OPEN_TASK_FROM_PROJECTS_EVENT, {
                                                            detail: { taskId: task.id },
                                                        }),
                                                    );
                                                }}
                                            >
                                                {task.name || task.id}
                                            </Link>
                                        )}
                                    />
                                ))
                            }
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Select todos"
                                    placeholder="Start typing task name"
                                />
                            )}
                        />

                        <Divider />

                        <Box ref={assetsSectionRef} sx={{ scrollMarginTop: 80 }}>
                            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                                <Typography variant="subtitle1">Assets selector (autocomplete)</Typography>
                                <Button
                                    size="small"
                                    variant={showLinkedAssetsOnly ? 'contained' : 'outlined'}
                                    onClick={() => setShowLinkedAssetsOnly((prev) => !prev)}
                                >
                                    {showLinkedAssetsOnly ? 'Showing linked only' : 'Show linked only'}
                                </Button>
                            </Stack>
                        </Box>
                        <Autocomplete
                            multiple
                            options={filteredAssetsOptions}
                            loading={assetsQuery.isLoading}
                            value={selectedAssets}
                            onChange={(_event, value: ExternalAsset[]) => {
                                const ids = value.map((asset) => asset.id);
                                setSelectedAssetIds(ids);
                                setForm((prev) => ({ ...prev, assets_json: buildAssetsJson(ids) }));
                                setJsonError('');
                            }}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            getOptionLabel={(option) => `${option.title}${option.asset_type ? ` (${option.asset_type})` : ''}`}
                            renderTags={(value, getTagProps) =>
                                value.map((asset, index) => (
                                    <Chip {...getTagProps({ index })} key={asset.id} label={asset.title || asset.id} />
                                ))
                            }
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Select external assets"
                                    placeholder="Start typing asset title"
                                />
                            )}
                        />

                        <TextField
                            label="Assets JSON"
                            multiline
                            minRows={7}
                            value={form.assets_json}
                            helperText={jsonError || 'Can be JSON or plain text. If JSON contains "asset_ids", selector syncs automatically.'}
                            onChange={(e) => {
                                const value = e.target.value;
                                setForm((prev) => ({ ...prev, assets_json: value }));

                                if (!value.trim()) {
                                    setSelectedAssetIds([]);
                                    setJsonError('');
                                    return;
                                }

                                try {
                                    JSON.parse(value);
                                    setSelectedAssetIds(parseAssetIds(value));
                                    setJsonError('');
                                } catch {
                                    setJsonError('Not valid JSON (plain text is still allowed).');
                                }
                            }}
                            fullWidth
                        />

                        <Box>
                            <Typography variant="subtitle2" gutterBottom>
                                Linked summary
                            </Typography>
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                <Button
                                    size="small"
                                    variant="text"
                                    component={Link}
                                    to={tasksSummaryHref}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Todos linked: {selectedTaskIds.length}
                                </Button>
                                <Typography variant="body2" color="text.secondary">|</Typography>
                                <Button
                                    size="small"
                                    variant="text"
                                    component={Link}
                                    to={assetsSummaryHref}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Assets linked: {selectedAssetIds.length}
                                </Button>
                                <Typography variant="body2" color="text.secondary">|</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Notes linked: {selectedNoteIds.length}
                                </Typography>
                            </Stack>
                        </Box>
                    </Stack>
                </Paper>
            </Stack>

            {selectedProjectId && (
                <Paper variant="outlined" sx={{ mt: 2, p: 2 }}>
                    <Typography variant="h6" sx={{ mb: 1.5 }}>
                        Tasks for selected project
                    </Typography>
                    <Tasks
                        projectId={selectedProjectId}
                        onTasksChanged={handleTasksChangedInProjectView}
                    />
                </Paper>
            )}
        </Box>
    );
};
